import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useDagStore } from "@/app/hooks/useDagStore";
import { omitDefaultSearchParams, SEARCH_DEFAULTS } from "@/app/routes/index.route";
import { ProjectIcon } from "./ProjectIcon";

export function ProjectFilter({ className = "" }: { className?: string }) {
  const router = useRouterState();
  const isDashboard = router.location.pathname === "/";

  // Always call hooks unconditionally
  const search = useSearch({ strict: false });
  const allProjects = useDagStore(useShallow((state) => state.allProjects));
  const storeExcludedProjects = useDagStore(useShallow((state) => state.excludedProjects));
  const storeToggleProject = useDagStore(useShallow((state) => state.toggleProject));
  const navigate = useNavigate({ from: "/" });

  const [isExpanded, setIsExpanded] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Get excludedProjects from URL if on dashboard, otherwise from store
  const excludedProjectsArray = isDashboard
    ? (search?.excludedProjects ?? SEARCH_DEFAULTS.excludedProjects)
    : Array.from(storeExcludedProjects);
  const excludedProjects = useMemo(() => new Set(excludedProjectsArray), [excludedProjectsArray]);

  const onProjectToggle = (projectId: string) => {
    if (isDashboard && search) {
      const newExcludedProjects = new Set(excludedProjectsArray);
      if (newExcludedProjects.has(projectId)) {
        newExcludedProjects.delete(projectId);
      } else {
        newExcludedProjects.add(projectId);
      }
      void navigate({
        search: omitDefaultSearchParams({
          ...search,
          excludedProjects: Array.from(newExcludedProjects),
        }),
      });
    } else {
      storeToggleProject(projectId);
    }
  };

  // Filter projects based on search term
  const filteredProjects = useMemo(() => {
    if (!searchTerm) return allProjects;
    const lowerSearch = searchTerm.toLowerCase();
    return allProjects.filter((project) => project.projectName.toLowerCase().includes(lowerSearch));
  }, [allProjects, searchTerm]);

  // Get count of selected projects
  const selectedCount = allProjects.length - excludedProjects.size;

  if (allProjects.length === 0) return null;

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-sm font-semibold text-stone-700 hover:text-stone-900"
        >
          {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          Projects
        </button>
        <span className="text-xs text-stone-500">
          {selectedCount}/{allProjects.length}
        </span>
      </div>

      {isExpanded && (
        <div className="space-y-2">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-stone-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search projects..."
              className="w-full pl-7 pr-2 py-1 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Project list with scrollbar */}
          <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
            {filteredProjects.length === 0 ? (
              <p className="text-sm text-stone-500 italic">No projects found</p>
            ) : (
              filteredProjects.map((project) => (
                <label
                  key={project.id}
                  className="flex items-center space-x-2 p-1 rounded hover:bg-stone-50"
                >
                  <input
                    type="checkbox"
                    checked={!excludedProjects.has(project.id)}
                    onChange={() => onProjectToggle(project.id)}
                    className="h-4 w-4 text-blue-600 rounded border-stone-300 focus:ring-blue-500"
                    aria-label={`Filter by ${project.projectName} project`}
                  />
                  <span
                    className="text-sm text-stone-700 flex-1 truncate flex items-center gap-1"
                    title={project.projectName}
                  >
                    <ProjectIcon icon={project.icon} />
                    {project.projectName}
                  </span>
                  {project.status && (
                    <span className="text-xs text-stone-500">{project.status}</span>
                  )}
                </label>
              ))
            )}
          </div>

          {/* Quick actions */}
          <div className="flex gap-2 pt-1 border-t border-stone-200">
            <button
              type="button"
              onClick={() => {
                allProjects.forEach((p) => {
                  if (!excludedProjects.has(p.id)) {
                    onProjectToggle(p.id);
                  }
                });
              }}
              className="text-xs text-stone-600 hover:text-stone-800"
            >
              Clear all
            </button>
            <button
              type="button"
              onClick={() => {
                allProjects.forEach((p) => {
                  if (excludedProjects.has(p.id)) {
                    onProjectToggle(p.id);
                  }
                });
              }}
              className="text-xs text-stone-600 hover:text-stone-800"
            >
              Select all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
